import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Client, ClientField } from '../../domain/models';
import { ClientFaq } from '../../domain/models/client-faq';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root'
})
export class ClientService {
    latest = new BehaviorSubject<Client | undefined>(undefined);

    constructor(private apiService: ApiService) {}

    load() {
        if (this.latest.getValue() == undefined) this.reload();
    }

    reload() {
        this.load$().subscribe();
    }

    /**
     * Fetches the client and publishes it, setting `runtimeConfig.clientId` before anything
     * observes `latest`.  Used by the app initializer so that clientId is populated before
     * any component is constructed — several services read it straight off the global and
     * would otherwise request data for client 0.
     *
     * Errors are swallowed to `undefined`: a failed client fetch must not be able to block
     * application bootstrap indefinitely.  `load()` remains callable afterwards to retry.
     */
    load$(): Observable<Client | undefined> {
        return this.getClient(runtimeConfig.clientCode).pipe(
            tap((client) => {
                runtimeConfig.clientId = client.clientId;
                // Environment configuration that rides along on this response so the CAPTCHA
                // widget can be rendered without baking the key into the bundle.
                runtimeConfig.turnstileSiteKey = client.turnstileSiteKey;
                this.latest.next(client);
            }),
            catchError(() => of(undefined))
        );
    }

    private getClient(clientCode: string): Observable<Client> {
        return this.apiService.get(`/clients/${clientCode}`);
    }

    getClientFields(applicationPageNumber?: number): Observable<ClientField[]> {
        let params = new HttpParams();
        if (applicationPageNumber)
            params = params.set('applicationPageNumber', applicationPageNumber);
        return this.apiService.get(
            `/clients/${runtimeConfig.clientId}/Fields`,
            params
        );
    }

    getFaqs(): Observable<ClientFaq[]> {
        return this.apiService.get(`/clients/${runtimeConfig.clientId}/faqs`);
    }
}
